import type { NotificationAction } from '../../../../store/slices/notifications';
import { showPopup } from '../../../../store/slices/templatesConfig';

// Re-export type for consumers
export type { NotificationAction } from '../../../../store/slices/notifications';

// Frontend method registry - predefined methods available for notification actions
const FRONTEND_METHODS = {
    displayModal: (maybeId: string, context?: any) => {
        const identifier = context?.currentNotification?.identifier;
        const chosen = identifier || maybeId || 'default-notification';
        if (context?.dispatch) {
            const storeObj: any = (window as any).store || null;
            let alreadyVisible = false;
            if (storeObj) {
                try {
                    const pv = storeObj.getState().templatesConfig.popupVisibility;
                    alreadyVisible = !!pv[chosen];
                } catch { }
            }
            if (!alreadyVisible) {
                // Include the triggering notification in the payload so popup can read it
                context.dispatch(showPopup({ popupId: chosen, notification: context?.currentNotification }));
            }
        }
    },
};

// Decide if a URL is external
const isExternalUrl = (url: string) => /^(https?:)?\/\//i.test(url);

// Perform redirect using best available mechanism (React Router if provided, fallback to location)
const performRedirect = (rawUrl?: string, context?: { navigate?: (to: string) => void; }) => {
    if (!rawUrl) return false;
    const url = rawUrl.trim();

    if (!isExternalUrl(url) && context?.navigate) {
        try {
            context.navigate(url);
            return true;
        } catch { }
    }

    const w: any = window as any;
    if (!isExternalUrl(url)) {
        if (typeof w.routerNavigate === 'function') {
            try { w.routerNavigate(url); return true; } catch { }
        }
        if (w.__APP_HISTORY__?.push) {
            try { w.__APP_HISTORY__.push(url); return true; } catch { }
        }
    }

    try {
        if (isExternalUrl(url)) {
            window.location.assign(url);
        } else {
            const path = url.startsWith('/') ? url : '/' + url;
            window.location.assign(path);
        }
        return true;
    } catch {
        return false;
    }
};

// Backend API helper
const callBackendEndpoint = async (handler: NotificationAction['handler']) => {
    try {
        const { method: rawMethod = 'GET', url, payload } = handler as any;
        const method = (rawMethod || 'GET').toUpperCase();
        if (!url) throw new Error('URL is required for backend calls');

        const isBodyMethod = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);
        const headers: Record<string, string> = { Accept: 'application/json' };
        let body: any = undefined;

        if (isBodyMethod && payload != null) {
            if (payload instanceof FormData) {
                body = payload; // Let browser set multipart boundary
            } else if (typeof payload === 'object') {
                headers['Content-Type'] = 'application/json';
                body = JSON.stringify(payload);
            } else {
                headers['Content-Type'] = 'text/plain';
                body = String(payload);
            }
        }

        try {
            const storeObj: any = (window as any).store || null;
            const token: string | undefined = storeObj?.getState?.()?.authentication?.access_token;
            if (token && !headers['Authorization']) {
                try {
                    headers['Authorization'] = `Bearer ${token}`;
                } catch { }
            }
        } catch { }

        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 30000);
        const response = await fetch(url, { method, headers, body, signal: controller.signal });
        clearTimeout(timeout);

        if (!response.ok) {
            const text = await response.text().catch(() => '');
            throw new Error(`HTTP ${response.status} ${response.statusText}${text ? ' - ' + text : ''}`);
        }

        const ct = response.headers.get('content-type') || '';
        const data = ct.includes('application/json') ? await response.json() : await response.text();
        return { success: true, data };
    } catch (error: unknown) {
        const message = error instanceof Error ? error.message : String(error);
        return { success: false, error: message };
    }
};

// Main action executor - generic name
export const executeAction = async (
    action: NotificationAction,
    onComplete?: () => void,
    context?: { dispatch?: any; currentNotification?: any; navigate?: (to: string) => void; }
): Promise<void> => {
    try {
        const handlerType = action.handler?.type;
        let shouldDismiss = false;
        switch (handlerType) {
            case 'callFrontendMethod': {
                if (action.handler?.method && FRONTEND_METHODS[action.handler.method as keyof typeof FRONTEND_METHODS]) {
                    const method = FRONTEND_METHODS[action.handler.method as keyof typeof FRONTEND_METHODS] as (...args: any[]) => unknown;
                    const params: any[] = action.handler.params || [];
                    const finalParams: any[] = context ? [...params, context] : params;
                    await method(...finalParams);
                }
                shouldDismiss = true;
                break;
            }
            case 'callBackendEndpoint': {
                const result = await callBackendEndpoint(action.handler);
                if (action.handler?.dismissOnSuccess && result.success) shouldDismiss = true;
                break;
            }
            case 'redirect': {
                const target = action.handler?.url;
                if (target) performRedirect(target, context);
                shouldDismiss = true;
                break;
            }
            default:
                break;
        }
        if (shouldDismiss) onComplete && onComplete();
    } catch (error) {
        console.error('Error executing action:', error);
    }
};

// Helper functions
export const getActionLabel = (action: NotificationAction): string => {
    if (!action.label) return action.id;
    if (typeof action.label === 'object') {
        const firstKey = Object.keys(action.label)[0];
        const value = firstKey ? (action.label as Record<string, any>)[firstKey] : '';
        return typeof value === 'string' && value ? value : action.id;
    }
    return action.label;
};

export const getActionsByType = (actions: NotificationAction[] = [], type: NotificationAction['type']) => {
    return actions.filter((action) => action.type === type);
};

export const getClickAction = (actions: NotificationAction[] = []) => {
    return actions.find((action) => action.type === 'toast_click' && action.style === 'invisible');
};

export const getVisibleActions = (actions: NotificationAction[] = []) => {
    return actions.filter((action) => action.type !== 'toast_click' || action.style !== 'invisible');
};

export const getPrimaryActions = (actions: NotificationAction[] = []) => {
    return getVisibleActions(actions).filter((a) => a.type === 'primary');
};
export const getSecondaryActions = (actions: NotificationAction[] = []) => {
    return getVisibleActions(actions).filter((a) => a.type === 'secondary');
};

export const getNotificationPrimaryAction = (notification?: { actions?: NotificationAction[]; } | null) => {
    const actions = notification?.actions || [];
    const list = getPrimaryActions(actions);
    return list.length ? list[0] : null;
};

export const getNotificationSecondaryAction = (notification?: { actions?: NotificationAction[]; } | null) => {
    const actions = notification?.actions || [];
    const list = getSecondaryActions(actions);
    return list.length ? list[0] : null;
};

export const executeNotificationActionWrapper = async (
    notification: any,
    action: NotificationAction | undefined | null,
    context: { dispatch?: any; navigate?: (to: string) => void; onDismiss?: () => void; } = {}
): Promise<void> => {
    if (!action) return;
    const { dispatch, navigate, onDismiss } = context;
    await executeAction(action, onDismiss, { dispatch, currentNotification: notification, navigate });
};

export { FRONTEND_METHODS };
